import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { PatientService } from '../../core/api/patient.service';
import { PAMPRestAPIService } from '../../core/api/pamp-rest-api.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LocaleDatePipe } from '../../shared/shared-pipes/locale-date.pipe';

// A test is sent, then it either comes back or it does not. This set is closed, which is why it
// drives the filter — unlike the test type, which the patient app can extend at any time.
export type SelfTestState = 'awaiting' | 'completed' | 'expired';
export type SelfTestFilter = 'all' | SelfTestState;

// Only blink-rate tests carry a clinical threshold today. Any other type gets no verdict at all
// rather than a misleading "not at risk".
const RISK_SCORED_TYPES = ['BlinkTest', 'PartailBlinkTest'];
const BLINK_RATE_KEY = 'Blink Rate';
const RESULT_KEYS = ['Result', 'result'];
const MAX_VISIBLE_METRICS = 4;

export interface SelfTestMetric {
  label: string;
  value: string;
}

export interface SelfTestRow {
  typeLabel: string;
  state: SelfTestState;
  sentOnLabel: string;
  completedOnLabel: string;
  isSameDay: boolean;
  visibleMetrics: SelfTestMetric[];
  hiddenMetricCount: number;
  resultText: string;
  hasRiskVerdict: boolean;
  isAtRisk: boolean;
}

@Component({
  selector: 'csi-patient-todo-history',
  templateUrl: './patient-todo-history.component.html',
  styleUrls: ['./patient-todo-history.component.scss'],
  providers: [LocaleDatePipe]
})
export class PatientTodoHistoryComponent implements OnInit, OnDestroy {
  // Set when rendered inside a record column that already shows a title and a refresh control.
  @Input() isEmbedded = false;

  public isHistoryLoading = true;
  public historyErrorMessage = '';

  public readonly selfTestFilters: SelfTestFilter[] = ['all', 'completed', 'awaiting', 'expired'];
  public readonly filterLabelMap: { [filter in SelfTestFilter]: string } = {
    all: 'All',
    awaiting: 'Awaiting',
    completed: 'Completed',
    expired: 'Expired'
  };

  public activeFilter: SelfTestFilter = 'all';
  public allRows: SelfTestRow[] = [];
  public visibleRows: SelfTestRow[] = [];
  public filterCountMap: { [filter: string]: number } = {};

  private paramSubscription: Subscription;
  private apiSubscription: Subscription;
  private todoCreatedSubscription: Subscription;

  constructor(
    private patientService: PatientService,
    private pampRestApiService: PAMPRestAPIService,
    private route: ActivatedRoute,
    private localeDatePipe: LocaleDatePipe
  ) {}

  ngOnInit(): void {
    if (this.route && this.route.parent) {
      this.paramSubscription = this.route.parent.params.subscribe(params => {
        const patientId = params['id'];
        if (patientId) {
          this.fetchPatientTodoHistory(patientId);
        } else {
          this.isHistoryLoading = false;
        }
      });
    } else {
      this.isHistoryLoading = false;
    }

    this.todoCreatedSubscription = this.pampRestApiService.pampTodoCreated$.subscribe(patientId => {
      const currentPatientId = this.patientService.patient && this.patientService.patient.id;
      if (patientId === currentPatientId) {
        this.refreshData();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.paramSubscription) {
      this.paramSubscription.unsubscribe();
    }
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
    if (this.todoCreatedSubscription) {
      this.todoCreatedSubscription.unsubscribe();
    }
  }

  public refreshData(): void {
    if (this.patientService.patient && this.patientService.patient.id) {
      this.fetchPatientTodoHistory(this.patientService.patient.id);
    }
  }

  public selectFilter(filter: SelfTestFilter): void {
    this.activeFilter = filter;
    this.applyFilter();
  }

  public trackByFilter(index: number, filter: SelfTestFilter): string {
    return filter;
  }

  public trackBySelfTestRow(index: number, row: SelfTestRow): string {
    return index + ':' + row.typeLabel;
  }

  public trackByMetricLabel(index: number, metric: SelfTestMetric): string {
    return metric.label;
  }

  public fetchPatientTodoHistory(patientId?: string): void {
    const targetPatientId =
      patientId || (this.patientService.patient && this.patientService.patient.id);

    if (!targetPatientId) {
      this.isHistoryLoading = false;
      return;
    }

    const enrollmentCode = this.patientService.patient && this.patientService.patient.patientAppId;

    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }

    this.isHistoryLoading = true;
    this.historyErrorMessage = '';

    this.apiSubscription = this.pampRestApiService
      .getPampTodoForPatient(targetPatientId, enrollmentCode)
      .subscribe(
        rawResponse => {
          this.isHistoryLoading = false;
          const rawItems = Array.isArray(rawResponse)
            ? rawResponse
            : (rawResponse && (rawResponse as any).data) || [];
          this.allRows = rawItems.map(rawItem => this.buildSelfTestRow(rawItem));
          this.applyFilter();
        },
        error => {
          this.isHistoryLoading = false;
          this.historyErrorMessage = 'Failed to load todo history.';
          console.error('Error fetching patient todo history:', error);
        }
      );
  }

  private applyFilter(): void {
    this.filterCountMap = {
      all: this.allRows.length,
      awaiting: this.countByState('awaiting'),
      completed: this.countByState('completed'),
      expired: this.countByState('expired')
    };
    this.visibleRows =
      this.activeFilter === 'all'
        ? this.allRows
        : this.allRows.filter(row => row.state === this.activeFilter);
  }

  private countByState(state: SelfTestState): number {
    return this.allRows.filter(row => row.state === state).length;
  }

  private buildSelfTestRow(rawItem: any): SelfTestRow {
    const state = this.resolveState(rawItem);
    const metrics = this.buildMetrics(rawItem.score);
    const sentOnLabel = this.formatDate(rawItem.createdOn);
    const completedOnLabel = this.formatDate(rawItem.completedOn);
    const hasRiskVerdict =
      state === 'completed' && RISK_SCORED_TYPES.indexOf(rawItem.questionnaireType) !== -1;

    return {
      typeLabel: rawItem.title || '',
      state: state,
      sentOnLabel: sentOnLabel,
      completedOnLabel: completedOnLabel,
      isSameDay: sentOnLabel === completedOnLabel,
      visibleMetrics: metrics.slice(0, MAX_VISIBLE_METRICS),
      hiddenMetricCount: Math.max(0, metrics.length - MAX_VISIBLE_METRICS),
      resultText: this.readResult(rawItem.score),
      hasRiskVerdict: hasRiskVerdict,
      isAtRisk: hasRiskVerdict && this.evaluateRisk(rawItem)
    };
  }

  private resolveState(rawItem: any): SelfTestState {
    const rawStatus = String(rawItem.status || '').toLowerCase();
    if (rawStatus === 'expired') {
      return 'expired';
    }
    if (rawStatus === 'completed' || rawItem.completedOn) {
      return 'completed';
    }
    return 'awaiting';
  }

  // Renders whatever keys the score bag carries, so a test type nobody has mapped still reads.
  private buildMetrics(score: any): SelfTestMetric[] {
    if (!score || typeof score !== 'object') {
      return [];
    }
    return Object.keys(score)
      .filter(scoreKey => RESULT_KEYS.indexOf(scoreKey) === -1)
      .map(scoreKey => ({ label: scoreKey, value: String(score[scoreKey]) }));
  }

  private readResult(score: any): string {
    if (!score || typeof score !== 'object') {
      return '';
    }
    const resultKey = RESULT_KEYS.filter(key => score[key])[0];
    return resultKey ? String(score[resultKey]) : '';
  }

  private evaluateRisk(rawItem: any): boolean {
    const score = rawItem.score;
    if (!score || score[BLINK_RATE_KEY] === undefined || score[BLINK_RATE_KEY] === null) {
      return false;
    }
    const blinkRate = Number(score[BLINK_RATE_KEY]);
    return blinkRate < 10 || blinkRate > 30;
  }

  private formatDate(dateValue: any): string {
    if (!dateValue) {
      return '';
    }
    const formattedDate = this.localeDatePipe.transform(dateValue, 'MMM d, y');
    return formattedDate ? formattedDate : '';
  }
}
